/* eslint-disable @tencent/i18next/no-literal-string */
import { ITextOverlapResult } from '@src/slide/application/service/slide-rating-and-improve/text/text-analyzer.interface';
import {
    AnalyzerResultDescriptionAndToSelectModel,
    AnalyzerResultType, IElementNode, ISlideNode,
    ITextElementBoxNode,
} from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer.interface';
import { Quadtree } from '@src/slide/infrastructure/module/independent/quadtree/quadtree';
import { Rectangle } from '@src/slide/infrastructure/module/independent/quadtree/rectangle';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import {
    getOffsetOfTextBox, isStringVisible,
} from '@src/slide/application/service/slide-rating-and-improve/text/calculate/text-box-calculate';
import { twipsToPx, ITextBox, IRect } from '@tencent/alloy-editor';
import { getFrameAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import {
    checkRectIntersection,
} from '@src/slide/application/service/slide-rating-and-improve/text/calculate/quadtree-calculate';
import {
    ITextElementOverlapResult,
} from '@src/slide/application/service/slide-rating-and-improve/text/text-overlap/text-overlap-analyzer.interface';
import { FillType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { transaction as sdkTransaction } from '@src/slide/domain/slide-app';
import { isElementModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import CommonTips, { CommonTipsType } from '@com/common-tips';
import i18n from '@i18next/i18n';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';

export class TextOverlapAnalyzer implements ITextOverlapResult {
    public resultInfo: Array<ITextElementOverlapResult>;
    public resultType: AnalyzerResultType;
    public analyzerName: string;
    public description: string;
    private textQuadtree: Quadtree<Rectangle<ITextElementBoxNode>> | undefined;
    private currentSlideIndex: number;
    private presentation: IPresentationModel;
    private changedElementMap: Map<IElementBaseModel, boolean>;
    private showResult: Array<AnalyzerResultDescriptionAndToSelectModel> = [];

    public constructor(analyzerName: string, description: string) {
        this.analyzerName = analyzerName;
        this.description = description;
        this.reset();
    }

    public resetWithPresentation(presentation: IPresentationModel) {
        this.reset();
        this.presentation = presentation;
        this.textQuadtree = new Quadtree({
            width: this.presentation.props.slideWidth,
            height: this.presentation.props.slideHeight,
        });
    }

    public onSlideEnd(slideNode: ISlideNode) {
        this.textQuadtree?.clear();
    }

    // eslint-disable-next-line complexity
    public getDescriptionWithSelect(): Array<AnalyzerResultDescriptionAndToSelectModel> | undefined {
        if (this.resultType === AnalyzerResultType.SUCCESS) return undefined;
        if (this.showResult.length > 0) return this.showResult;
        const result: Array<AnalyzerResultDescriptionAndToSelectModel> = [];
        for (let i = 0; i < this.resultInfo.length; i++) {
            const current = this.resultInfo[i];
            const currentNode = current.textNodeRect.data as ITextElementBoxNode;
            const currentBox = currentNode.textBox;
            const currentSlideIndex = currentNode.slideNode.slideIndex;
            const description = i18n.t(
                '第{0}页\'{1}\'存在和其他文本重叠情况',
                { 0: currentSlideIndex + 1, 1: currentBox.text },
            );
            result.push({
                description,
                selectable: true,
                selectSlide: currentNode.slideNode.slide,
                selectElement: currentNode.element,
                handleProblem: this.handleItemClick,
            });
        }
        this.showResult = result;
        return result;
    }

    public reset() {
        this.resultInfo = [];
        this.showResult = [];
        this.resultType = AnalyzerResultType.SUCCESS;
        this.textQuadtree?.clear();
        this.changedElementMap = new Map<IElementBaseModel, boolean>();
    }

    public checkNewElement(elementNode: IElementNode) {
        const elementModel = elementNode.element;
        const frame = elementModel.getFrame().frameOnSlide();
        let bounds = frame;
        let hasRotate = false;
        if (frame.rotate !== undefined && frame.rotate !== 0) {
            hasRotate = true;
            bounds = getFrameAfterRotate(frame, frame.rotate);
        }
        const elementRect = new Rectangle({
            x: bounds.left, y: bounds.top,
            width: bounds.width,
            height: bounds.height,
        });
        if (elementModel.type === UIModelType.SHAPE) {
            const fillType = elementModel.getBackgroundFill().getShownFillType();
            if (fillType === undefined || fillType === FillType.NoFill) {
                // 无填充不用处理
                return;
            }
            this.checkToDeleteTextRects(elementRect);
        } else if (elementModel.type === UIModelType.IMAGE
            || elementModel.type === UIModelType.TABLE
            || elementModel.type === UIModelType.CHART
        ) {
            this.checkToDeleteTextRects(elementRect);
        }
    }

    /**
     * 检测无填充的文本之间是否有重叠，会造成看不清
     * @param textElementBoxNode
     */
    public checkNewTextElementBoxNode(textElementBoxNode: ITextElementBoxNode) {
        if (textElementBoxNode.slideNode.slideIndex !== this.currentSlideIndex) {
            this.currentSlideIndex = textElementBoxNode.slideNode.slideIndex;
            this.textQuadtree?.clear();
        }
        if (this.checkNeedFilter(textElementBoxNode)) return;

        const actualRect = this.getActualRectFromTextBox(textElementBoxNode.textBox);
        const offset = getOffsetOfTextBox(textElementBoxNode.textBox);
        const elementModel = textElementBoxNode.element;
        const elementFrame = elementModel.getFrame().frameOnSlide();
        if (elementFrame.rotate !== undefined && elementFrame.rotate !== 0) {
            // 旋转比较复杂，先不支持处理
            // const center = {
            //     x: elementFrame.left + (elementFrame.width / 2),
            //     y: elementFrame.top + (elementFrame.height / 2),
            // };
            // boxBounds = rotateRectWithPoint(boxBounds, center, elementFrame.rotate);
            return;
        }
        const boxBounds = {
            left: twipsToPx(offset.x) + actualRect.x + elementFrame.left,
            top: twipsToPx(offset.y) + actualRect.y + elementFrame.top,
            width: actualRect.width,
            height: actualRect.height,
        };
        const textBound = new Rectangle({
            x: boxBounds.left,
            y: boxBounds.top,
            width: Math.floor(boxBounds.width),
            height: Math.floor(boxBounds.height),
        });
        const rects = this.textQuadtree?.retrieve(textBound) as Array<Rectangle<ITextElementBoxNode>>;
        const textBoxRect = new Rectangle<ITextElementBoxNode>({
            ...textBound,
            data: textElementBoxNode,
        });
        this.checkTextAndBackRects(textBoxRect, rects);
        this.textQuadtree?.insert(textBoxRect);
    }

    private checkTextAndBackRects(
        textNodeRect: Rectangle<ITextElementBoxNode>,
        rects: Array<Rectangle<ITextElementBoxNode>>,
    ): boolean {
        const sortedRects = rects.sort((a, b) => {
            const pathA = (a.data as ITextElementBoxNode).path;
            const pathB = (b.data as ITextElementBoxNode).path;
            const pathBLength = pathB.length;
            for (let i = 0; i < pathA.length; i++) {
                if (pathBLength < i) {
                    return 1;
                }
                if (pathA[i] !== pathB[i]) {
                    return pathB[i] - pathA[i];
                }
            }
            // 元素路径比较相同，进一步比较text路径
            const textPathA = (a.data as ITextElementBoxNode).textBoxPath;
            const textPathB = (b.data as ITextElementBoxNode).textBoxPath;
            const textPathBLength = textPathB.length;
            for (let j = 0; j < textPathA.length; j++) {
                if (textPathBLength < j) {
                    return 1;
                }
                if (textPathA[j] !== textPathB[j]) {
                    return textPathB[j] - textPathA[j];
                }
            }
            return 0;
        });
        for (let i = 0; i < sortedRects.length; i++) {
            // 判断是否重叠，同一个文本框内的暂时先忽略
            if ((textNodeRect.data as ITextElementBoxNode).element
                !== (sortedRects[i].data as ITextElementBoxNode).element
                && checkRectIntersection(textNodeRect, sortedRects[i])) {
                this.resultType = AnalyzerResultType.ERROR;
                this.resultInfo.push({
                    textNodeRect,
                    backNodeRect: sortedRects[i],
                    sortedRects,
                });
                return true;
            }
        }
        return false;
    }

    private checkNeedFilter(textElementBoxNode: ITextElementBoxNode): boolean {
        if (textElementBoxNode.element.type === UIModelType.CHART) return true;
        // 不可见字符直接过滤掉
        if (!isStringVisible(textElementBoxNode.textBox.text)) return true;
        if (textElementBoxNode.textBox.textStyle.textBackground !== 'transparent') return true;
        const elementFill = textElementBoxNode.element.getBackgroundFill();
        // 有图形的背景做填充，暂时放过不检测。
        const fillType = elementFill?.getShownFillType();
        if (fillType !== FillType.NoFill && fillType !== undefined) return true;
        // 文字无填充且无边框，过滤掉
        // @ts-ignore
        const documentElementPath: Array<number> | undefined = textElementBoxNode.textBox.textStyle?.getCustomizeProp?.('slatePath');
        if (documentElementPath) {
            const textElementStyle = textElementBoxNode.textDocumentElement
                .children[documentElementPath[0]].children[documentElementPath[1]];
            if (textElementStyle.noFill && !textElementStyle.border) return true;
        }
        return false;
    }

    private getActualRectFromTextBox(textBox: ITextBox): Rectangle {
        const { rect, glyphs } = textBox;
        let actualRect = new Rectangle({
            x: 0, y: 0,
            width: 0, height: 0,
        });
        let infoEnough = true;
        if (glyphs) {
            for (let i = 0; i < glyphs.length; i++) {
                const currentTextMetrics = glyphs[i].textMetrics;
                if (currentTextMetrics?.actualBoundingBoxAscent === undefined) {
                    infoEnough = false;
                    break;
                }
                // 排版层为了使排版后字间距和 office ppt 一致，处理竖排和堆积的时候并不是单纯调换文字的宽高，而是进行了重新计算，导致文字的实际高度 !== fontAscent + fontDescent。
                // 这种场景下如果使用 fontAscent 来计算文字实际位置的偏移量会出错，所以改为排版层重新计算过的 glyph[i].ascent。
                const currentY = twipsToPx(glyphs?.[i].ascent) - currentTextMetrics.actualBoundingBoxAscent;
                const currentHeight = currentTextMetrics.actualBoundingBoxAscent
                    + currentTextMetrics.actualBoundingBoxDescent;
                if (i === glyphs.length - 1) {
                    actualRect.width += currentTextMetrics.actualBoundingBoxRight;
                } else {
                    actualRect.width += currentTextMetrics.width;
                }
                if (i === 0) {
                    actualRect.y = currentY;
                    actualRect.height = currentHeight;
                    actualRect.x = -currentTextMetrics.actualBoundingBoxLeft;
                    actualRect.width -= actualRect.x;
                } else {
                    actualRect.y = Math.min(actualRect.y, currentY);
                    actualRect.height = Math.max(actualRect.height, currentHeight);
                }
            }
        }

        // @ts-ignore
        const rotateNum = textBox.rotate;
        if (rotateNum !== undefined) {
            actualRect = this.getRectAfterRotate(actualRect, rotateNum, rect);
        }
        if (!infoEnough) {
            if (rotateNum === 90 || rotateNum === 270) {
                actualRect.x = twipsToPx(rect.width * 0.1);
                actualRect.width = twipsToPx(rect.width * 0.8);
                actualRect.height = twipsToPx(rect.height);
            } else {
                actualRect.y = twipsToPx(rect.height * 0.1);
                actualRect.width = twipsToPx(rect.width);
                actualRect.height = twipsToPx(rect.height * 0.8);
            }
        }
        return actualRect;
    }

    private getRectAfterRotate(rect: Rectangle, rotate: number, wrapperRect: IRect): Rectangle {
        if (rotate === 90) {
            return new Rectangle({
                x: twipsToPx(wrapperRect.width) - rect.y - rect.height,
                y: rect.x,
                width: rect.height,
                height: rect.width,
            });
        }
        if (rotate === 270) {
            return new Rectangle({
                x: rect.y,
                y: twipsToPx(wrapperRect.height) - rect.x - rect.width,
                width: rect.height,
                height: rect.width,
            });
        }
        return rect;
    }

    private handleItemClick = (index: number) => {
        if (this.showResult[index].selectSlide?.sdkData
            && this.showResult[index].selectElement?.sdkData) {
            const currentItem = this.resultInfo[index];
            const { element } = currentItem.textNodeRect.data as ITextElementBoxNode;
            if (this.changedElementMap.get(element)) {
                CommonTips.getInstance().show({
                    message: i18n.t('元素位置已被调整，可重新检测验证调整结果'),
                    commonTipsType: CommonTipsType.INFO,
                    block: false,
                    autoClose: true,
                });
                this.removeOneResult(index);
                return;
            }
            const frame = this.getToUpdateFrame(currentItem);
            sdkTransaction(() => {
                if (!isElementModel(element)) return;
                element.getFrame().updateFrame({ frame });
                this.changedElementMap.set(element, true);
            });
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.AUTO_BEAUTIFY,
                action: TDWActionType.BEAUTIFY_TEXT_OVERLAP,
            });
        }
        this.removeOneResult(index);
        return;
    };

    private removeOneResult(index: number) {
        this.resultInfo.splice(index, 1);
        this.showResult.splice(index, 1);
        if (this.showResult.length === 0) this.resultType = AnalyzerResultType.SUCCESS;
    }

    private getToUpdateFrame(result: ITextElementOverlapResult): Partial<IFrame> {
        const textRect = result.textNodeRect;
        const backsRect = result.backNodeRect;
        const backElement = (backsRect.data as ITextElementBoxNode).element;
        const backElementFrame = backElement.getFrame().frameOnSlide();
        const offset = {
            x: 0,
            y: 0,
        };
        const changedX = Math.ceil(backsRect.x + Math.max(backsRect.width, backElementFrame.width) - textRect.x);
        if (changedX > -1 && changedX + textRect.width + textRect.x <= this.presentation.props.slideWidth + 1) {
            offset.x = changedX;
        }
        if (offset.x === 0) {
            const changedY = Math.ceil(backsRect.y + Math.max(backsRect.height, backElementFrame.height) - textRect.y);
            if (changedY > -1 && changedY + textRect.height + textRect.y <= this.presentation.props.slideHeight + 1) {
                offset.y = changedY;
            }
        }
        if (offset.x === 0 && offset.y === 0) {
            offset.x = changedX;
        }

        const { element } = result.textNodeRect.data as ITextElementBoxNode;
        const currentFrame = element.getFrame().getFrameInfo();
        if (element.parent?.type === UIModelType.GROUP) {
            const parentGroup = (element.parent as IGroupModel);
            const relativeScale = parentGroup.getFrame().getScaleRelativeToSlide();
            return {
                left: currentFrame.left + (offset.x * relativeScale.scaleX),
                top: currentFrame.top + (offset.y * relativeScale.scaleY),
            };
        }
        return {
            left: currentFrame.left + offset.x,
            top: currentFrame.top + offset.y,
        };
    }

    private checkToDeleteTextRects(elementRect: Rectangle) {
        const overTextBoxArray = this.textQuadtree?.retrieve(elementRect);
        overTextBoxArray?.forEach((textBox) => {
            if (checkRectIntersection(elementRect, textBox)) {
                this.textQuadtree?.remove(textBox);
            }
        });
    }
}
